/**
 * Hiring React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { hiringService } from '../services';

const HIRING_KEY = 'hiring';
const APPLICATIONS_KEY = 'applications';
const STAGES_KEY = 'stages';
const ACTIVITIES_KEY = 'activities';

// Pipeline Stages
export function usePipelineStages() {
  return useQuery({
    queryKey: [HIRING_KEY, STAGES_KEY],
    queryFn: () => hiringService.getPipelineStages?.() || Promise.resolve({ stages: [] }),
    staleTime: 10 * 60 * 1000,
  });
}

// Applications
export function useApplications(params = {}) {
  return useQuery({
    queryKey: [HIRING_KEY, APPLICATIONS_KEY, params],
    queryFn: () => hiringService.getApplications(params),
    staleTime: 1 * 60 * 1000,
  });
}

export function useApplicationsByStage(stage) {
  return useQuery({
    queryKey: [HIRING_KEY, APPLICATIONS_KEY, 'stage', stage],
    queryFn: () => hiringService.getApplicationsByStage?.(stage) || hiringService.getApplications({ stage }),
    enabled: !!stage,
    staleTime: 30 * 1000,
  });
}

export function useApplication(id) {
  return useQuery({
    queryKey: [HIRING_KEY, APPLICATIONS_KEY, id],
    queryFn: () => hiringService.getApplicationById(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateApplication() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: hiringService.createApplication,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HIRING_KEY, APPLICATIONS_KEY] });
    },
  });
}

export function useUpdateApplication() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => hiringService.updateApplication(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [HIRING_KEY, APPLICATIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [HIRING_KEY, APPLICATIONS_KEY, variables.id] });
    },
  });
}

// Stage Management - supports both old and new API patterns
export function useUpdateApplicationStage() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, stage, reason }) => {
      // Try new API first, fall back to old
      if (hiringService.updateApplicationStage) {
        return hiringService.updateApplicationStage(id, stage, reason);
      }
      return hiringService.updateStage(id, stage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HIRING_KEY, APPLICATIONS_KEY] });
    },
  });
}

export function useDeleteApplication() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, reason }) => {
      if (hiringService.deleteApplication.length === 2) {
        return hiringService.deleteApplication(id, reason);
      }
      return hiringService.deleteApplication(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HIRING_KEY, APPLICATIONS_KEY] });
    },
  });
}

// Activities - supports both naming conventions
export function useActivities(applicationId) {
  return useQuery({
    queryKey: [HIRING_KEY, APPLICATIONS_KEY, applicationId, ACTIVITIES_KEY],
    queryFn: () => hiringService.getActivities(applicationId),
    enabled: !!applicationId,
  });
}

export function useApplicationActivities(id) {
  return useActivities(id);
}

export function useAddActivity() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ applicationId, id, data }) => {
      const appId = applicationId || id;
      return hiringService.addActivity(appId, data);
    },
    onSuccess: (_, variables) => {
      const appId = variables.applicationId || variables.id;
      queryClient.invalidateQueries({ queryKey: [HIRING_KEY, APPLICATIONS_KEY, appId, ACTIVITIES_KEY] });
      queryClient.invalidateQueries({ queryKey: [HIRING_KEY, APPLICATIONS_KEY, appId] });
    },
  });
}

// Convert to Employee/User - supports both naming conventions
export function useConvertToEmployee() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ applicationId, id, data }) => {
      const appId = applicationId || id;
      if (hiringService.convertToEmployee) {
        return hiringService.convertToEmployee(appId, data);
      }
      return hiringService.convertToUser?.(appId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HIRING_KEY, APPLICATIONS_KEY] });
    },
  });
}

export function useConvertToUser() {
  return useConvertToEmployee();
}

// Recruiters
export function useRecruiters() {
  return useQuery({
    queryKey: [HIRING_KEY, 'recruiters'],
    queryFn: () => hiringService.getRecruiters(),
    staleTime: 5 * 60 * 1000,
  });
}
